import type {
  JobPostingForm,
  OpeningChannelResult,
  OpeningResult,
  PostingResultData,
  PostingResultScenario,
} from '../types';
import type {
  SourcingPurchaseBulkCreateRequest,
  SourcingPurchaseBulkCreateResponse,
} from 'api-clients/monolith';

/**
 * Maps the bulk-create API response back to a structured per-opening,
 * per-channel result using the original request array indices.
 *
 * The API returns `failed_records[].index` which maps 1:1 to the
 * `sourcing_purchases` array that was sent in the request.
 */
export function derivePostingResult(
  formData: JobPostingForm,
  requestBody: SourcingPurchaseBulkCreateRequest,
  response: SourcingPurchaseBulkCreateResponse,
): PostingResultData {
  const failedIndices = new Set(
    response.failed_records
      .map(r => r.index)
      .filter((idx): idx is number => idx != null),
  );

  const channelsByKey = formData.channels;

  // Build a lookup: channelSlug → display metadata from form state
  const channelMetaBySlug = new Map<
    string,
    {
      name: string;
      displayTitle: string;
      logoSquareUrl: string | null;
      logoRectangleUrl: string | null;
    }
  >();
  for (const channelState of Object.values(channelsByKey)) {
    if (!channelMetaBySlug.has(channelState.slug)) {
      channelMetaBySlug.set(channelState.slug, {
        name: channelState.name,
        displayTitle: channelState.displayTitle,
        logoSquareUrl: channelState.vonqDetails?.logoSquareUrl ?? null,
        logoRectangleUrl: channelState.vonqDetails?.logoRectangleUrl ?? null,
      });
    }
  }

  // Group results by opening (funnel_external_id)
  const openingMap = new Map<string, OpeningChannelResult[]>();
  const purchases = requestBody.sourcing_purchases;

  for (let i = 0; i < purchases.length; i++) {
    const purchase = purchases[i];
    const openingId = purchase.funnel_external_id;
    const status: 'posted' | 'failed' = failedIndices.has(i)
      ? 'failed'
      : 'posted';

    const meta = channelMetaBySlug.get(purchase.sourcing_channel_slug);

    const channelResult: OpeningChannelResult = {
      channelSlug: purchase.sourcing_channel_slug,
      channelName: meta?.name ?? purchase.sourcing_channel_slug,
      displayTitle: meta?.displayTitle ?? meta?.name ?? purchase.sourcing_channel_slug,
      logoSquareUrl: meta?.logoSquareUrl ?? null,
      logoRectangleUrl: meta?.logoRectangleUrl ?? null,
      status,
      budget: status === 'posted' ? purchase.total_budget ?? 0 : 0,
    };

    const existing = openingMap.get(openingId);
    if (existing) {
      existing.push(channelResult);
    } else {
      openingMap.set(openingId, [channelResult]);
    }
  }

  // Build per-opening results
  const openings: OpeningResult[] = [];
  let postedOpenings = 0;
  let failedOpenings = 0;
  let totalSpend = 0;
  const uniqueChannelSlugs = new Set<string>();

  openingMap.forEach((channels, openingId) => {
    const title = formData.jobDetails[openingId]?.title ?? openingId;

    openings.push({ openingId, openingTitle: title, channels });

    const allPosted = channels.every(
      (c: OpeningChannelResult) => c.status === 'posted',
    );
    const allFailed = channels.every(
      (c: OpeningChannelResult) => c.status === 'failed',
    );

    if (allFailed) {
      failedOpenings += 1;
    } else if (!allPosted) {
      failedOpenings += 1;
    } else {
      postedOpenings += 1;
    }

    for (const ch of channels) {
      uniqueChannelSlugs.add(ch.channelSlug);
      totalSpend += ch.budget;
    }
  });

  const openingsSubmitted = openings.length;

  let scenario: PostingResultScenario;
  if (response.summary.failed === 0) {
    scenario = 'all-success';
  } else if (response.summary.created === 0) {
    scenario = 'all-failed';
  } else {
    scenario = 'partial';
  }

  return {
    scenario,
    summary: {
      openingsSubmitted,
      postedOpenings,
      failedOpenings,
      channels: uniqueChannelSlugs.size,
      totalSpend,
    },
    openings,
  };
}
